import { ORIGEM_PADRAO, type FiltrosDaLista } from '../../../api/filtrosDeGmud'
import { TERMOS } from '../../../lib/glossario'
import type { OrigemGmud, Prioridade, StatusGmud } from '../../../types/gmud'

/**
 * Os atalhos de triagem: os recortes que quase todo mundo faz, a um clique.
 *
 * Cada atalho escreve os MESMOS filtros do painel — não existe uma segunda consulta, nem um
 * parâmetro que só o chip conhece. É por isso que o chip aceso e o painel aberto nunca podem
 * discordar: o chip é uma forma curta de marcar as mesmas caixas. No sistema anterior a
 * listagem, o painel e a exportação tinham cada um o seu jeito de dizer "aguardando o comitê",
 * e as três telas mostravam contagens diferentes para a mesma pergunta.
 *
 * O que o atalho NÃO toca é o texto digitado (chamado e busca): quem procurou o chamado 1281 e
 * depois clica em "Em execução" quer as duas coisas, não perder o que digitou.
 */

/** A parte dos filtros que um atalho escreve. */
export interface Recorte {
  status: StatusGmud[]
  prioridade: Prioridade[]
  origem: OrigemGmud[]
}

export interface Triagem {
  /** Identificador estável. Não é rótulo: trocar o texto do chip não pode trocar a chave. */
  id: string
  rotulo: string
  /** Uma linha explicando o recorte, para quem não é de TI. Vira o `title` do chip. */
  ajuda: string
  recorte: Recorte
}

/**
 * O ponto de partida dos atalhos do processo: nenhum estado, nenhuma prioridade, e a procedência
 * padrão da listagem.
 *
 * A procedência entra aqui e não em cada atalho porque é o recorte que `/gmuds` já mostra sem
 * ninguém pedir (`ORIGEM_PADRAO`). Se ficasse de fora, o chip nunca casaria com o que está na URL
 * e a fileira inteira apagaria numa lista que está, sim, no recorte de um dos atalhos.
 */
const PADRAO: Recorte = { status: [], prioridade: [], origem: [...ORIGEM_PADRAO] }

export const TRIAGENS: Triagem[] = [
  {
    id: 'todas',
    rotulo: 'Todas deste sistema',
    ajuda:
      'Todas as solicitações abertas neste sistema, em qualquer etapa. O histórico trazido do ' +
      'sistema anterior fica no atalho ao lado.',
    recorte: PADRAO,
  },
  {
    id: 'aguardando-comite',
    rotulo: 'Aguardando o comitê',
    ajuda: `Já foram enviadas e esperam entrar em pauta ou sair da avaliação do ${TERMOS.cab.rotulo}.`,
    recorte: { ...PADRAO, status: ['SOLICITADA', 'EM_AVALIACAO_CAB'] },
  },
  {
    // Dois estados sob um rótulo só, e a ajuda diz isso em voz alta: entre implantar e validar
    // não há espera de ninguém — para quem faz triagem, as duas são "está andando". Separá-las
    // no chip obrigaria a clicar em dois para ver o mesmo conjunto de trabalho.
    id: 'em-execucao',
    rotulo: 'Em execução',
    ajuda: 'Aprovadas pelo comitê e em curso: sendo implantadas, ou em verificação depois da implantação.',
    recorte: { ...PADRAO, status: ['EM_EXECUCAO', 'EM_VERIFICACAO'] },
  },
  {
    // "Emergenciais" é uma pergunta operacional — o que está pegando fogo agora —, e a
    // procedência que ela precisa é a mesma dos outros atalhos do processo: as emergenciais de
    // 2019 são estado terminal e enterrariam as de hoje na primeira página. Quem procura
    // emergenciais antigas marca a procedência no painel, ou parte do atalho de histórico ao
    // lado; nos dois casos o recorte fica escrito na URL, não escondido no código.
    id: 'emergenciais',
    rotulo: 'Emergenciais',
    ajuda: 'Prioridade emergencial entre as solicitações abertas neste sistema.',
    recorte: { ...PADRAO, prioridade: ['EMERGENCIAL'] },
  },
  {
    // Exatamente o mesmo recorte do item de menu (`/gmuds?origem=MIGRADO_V1`), e tem de
    // continuar sendo: `itemAtivo` só acende o item do menu quando o parâmetro está aplicado.
    id: 'historico',
    rotulo: TERMOS.historicoMigrado.rotulo,
    ajuda: TERMOS.historicoMigrado.ajuda,
    recorte: { ...PADRAO, origem: ['MIGRADO_V1'] },
  },
]

/**
 * Qual atalho corresponde ao recorte que está na URL agora.
 *
 * Exige correspondência EXATA nos três critérios, procedência inclusive. Um filtro montado à mão
 * no painel não acende chip nenhum, e é isso que se quer: acender "Todas deste sistema" numa lista
 * que também traz o histórico faria o chip afirmar um recorte que não é o que está na tela.
 *
 * @param filtros critérios correntes
 * @returns o id do atalho correspondente, ou nulo quando o recorte é outro
 */
export function triagemAtiva(filtros: FiltrosDaLista): string | null {
  const encontrada = TRIAGENS.find(
    ({ recorte }) =>
      mesmoConjunto(recorte.status, filtros.status) &&
      mesmoConjunto(recorte.prioridade, filtros.prioridade) &&
      mesmoConjunto(recorte.origem, filtros.origem),
  )
  return encontrada?.id ?? null
}

/** Mesma coleção, independentemente da ordem em que os parâmetros vieram na URL. */
function mesmoConjunto(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((valor) => b.includes(valor))
}
